import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { message } from "antd";
import AttachmentButton from "./AttachmentButton";
import AttachmentsListModal from "./AttachmentsListModal";
import axios from "axios";
import { baseURL } from "../../../../globalUtils/axiosConfig";
import * as actions from "../../redux/actions";

const AttachmentContainer = ({
  recordType,
  record,
  id,
  registerId,
  color,
  additionalData = '',
  visibility,
  onAttachmentCountChange,
  onLocalAttachmentsChange,
  canAdd = true,
  disabled
}) => {
  const [delAttachKey, setDelAttachKey] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [localAttachments, setLocalAttachments] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const inputFile = useRef(null);
  const borderColor = color;
  const dispatch = useDispatch();

  // Generate a unique ID for the input element to avoid conflicts between rows
  const uniqueId = id || record?.id || record?.key || Math.random().toString(36).substr(2, 9);
  const inputId = `attachment-${recordType}-${uniqueId}`;

  const humanizeRecordType = () => {
    if (!recordType) return 'RECORD';
    return recordType
      .split('_')
      .map(word => word.toUpperCase())
      .join(' ');
  };

  let modalTitle;
  if (recordType === 'labs_stock_management') {
    modalTitle = `ATTACHMENTS`;
  } else {
    modalTitle = `${humanizeRecordType()} ATTACHMENTS`;
  }

  const createUrl = () => {
    return `${baseURL}/attachments`;
  }

  const deleteUrl = (recordId) => {
    return `${baseURL}/attachments/${recordId}`;
  }

  // Update local attachments when props change
  React.useEffect(() => {
    if (record?.attachments) {

      // Filter attachments based on additionalData
      let filteredAttachments = record.attachments;

      // Write filtering logic here

      setLocalAttachments(filteredAttachments);
    } else {
      setLocalAttachments([]);
    }
  }, [record, additionalData]);

  // Notify parent component when attachment count changes (only for filtered attachments)
  React.useEffect(() => {
    if (onAttachmentCountChange) {
      if (additionalData === "") {
        // Only count filtered ____________ attachments
        onAttachmentCountChange(localAttachments.length);
      }
    }
  }, [localAttachments.length, onAttachmentCountChange, additionalData]);

  const handleShowImage = async (image) => {
    if (image.isLocal) {
      let url = URL.createObjectURL(image);
      window.open(url, "_blank");
    } else {
      try {
        const hideLoading = message.loading("Opening attachment...", 0);

        const response = await axios({
          url: image.path,
          method: 'GET',
          responseType: 'blob',
        });

        const mimeType = response.headers['content-type'] || 'application/octet-stream';

        const blob = new Blob([response.data], { type: mimeType });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;

        if (mimeType.startsWith('image/') || mimeType === 'application/pdf') {
          a.target = '_blank';
        } else {
          a.download = image.name || `attachment_${Date.now()}`;
        }

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        setTimeout(() => window.URL.revokeObjectURL(url), 60000);

        hideLoading();
      } catch (error) {
        console.error('Error fetching attachment:', error);
        message.error(`Failed to load attachment: ${error.response?.data?.message || error.message}`);
      }
    }
  };

  const uploadFile = async (file) => {
    const attachableId = record?.id || record?.key;

    // Handle signed delivery note files locally (no API call)
    if (additionalData === 'signedDeliveryNote') {
      setUploading(true);

      try {
        // Create local attachment object with file data
        const newAttachment = {
          id: Date.now(),
          key: Date.now(),
          name: file.name,
          filename: file.name,
          originalName: file.name,
          size: file.size,
          mimetype: file.type,
          uploadedAt: new Date().toISOString(),
          isLocal: true,
          file: file, // Store the actual file object
          additionalData: { signedDeliveryNote: true }
        };

        const updatedAttachments = [...localAttachments, newAttachment];
        setLocalAttachments(updatedAttachments);

        // Return attachments to parent component
        if (onLocalAttachmentsChange) {
          onLocalAttachmentsChange(updatedAttachments);
        }

        message.success('Signed delivery note attachment added Successfully');

      } catch (error) {
        console.error('Local storage error:', error);
        message.error('Failed to add attachment locally');
      } finally {
        setUploading(false);
      }
      return;
    }

    // Check for attachableId for regular API uploads
    if (!attachableId) {
      message.error(`ID not found for ${humanizeRecordType()}`);
      return;
    }

    // Regular API upload flow - use PATCH for variance_report, POST for others
    if (recordType === 'variance_report') {
      try {
        setUploading(true);

        await dispatch(actions.updateVarianceReport(registerId, attachableId, { documents: [file] }));

        message.success('Attachment uploaded successfully');

        const newAttachment = {
          id: Date.now(),
          key: Date.now(),
          name: file.name,
          filename: file.name,
          size: file.size,
          uploadedAt: new Date().toISOString(),
          isLocal: false
        };
        const updatedAttachments = [...localAttachments, newAttachment];
        setLocalAttachments(updatedAttachments);
      } catch (error) {
        console.error('Upload error:', error);
        message.error('Failed to upload attachment');
      } finally {
        setUploading(false);
      }
      return;
    }

    // Regular API upload flow for other attachment types
    const formData = new FormData();
    let attachableType;
    if (recordType === 'labs_stock_management') {
      attachableType = 'LabsStockManagement';
    } // else if for the other record types

    formData.append('attachableType', attachableType);
    formData.append('attachableId', attachableId);
    formData.append('documents', file);

    try {
      setUploading(true);

      const response = await axios({
        method: 'POST',
        url: createUrl(),
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      message.success('Attachment uploaded successfully');

      // Update attachments with response or add to local state
      if (response.data && response.data.attachments) {
        setLocalAttachments(response.data.attachments);
      } else {
        // Fallback: add the uploaded file to local state with mock data
        const newAttachment = {
          id: Date.now(),
          key: Date.now(),
          name: file.name,
          filename: file.name,
          size: file.size,
          uploadedAt: new Date().toISOString(),
          isLocal: false
        };
        const updatedAttachments = [...localAttachments, newAttachment];
        setLocalAttachments(updatedAttachments);
      }
    } catch (error) {
      console.error('Upload error:', error);

      if (error.response) {
        const status = error.response.status;
        const message_text = error.response.data?.message || 'Upload failed';

        if (status === 404) {
          message.error('Progress step not found. Please check the configuration.');
        } else if (status === 413) {
          message.error('File too large. Please select a smaller file.');
        } else if (status >= 500) {
          message.error('Server error. Please try again later.');
        } else {
          message.error(`Upload failed: ${message_text}`);
        }
      } else if (error.request) {
        message.error('Network error. Please check your connection and server status.');
      } else {
        message.error('Upload failed. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const changeHandler = async (event) => {
    if (event.target.files.length < 1) {
      return;
    }

    // Upload each file immediately
    for (let i = 0; i < event.target.files.length; i++) {
      const file = event.target.files[i];
      await uploadFile(file);
    }

    // Clear the input
    event.target.value = null;
  };

  const handleDeleteImage = async (attachment) => {
    if (attachment?.isLocal) {
      // For local files (____________), just filter them out
      const filteredImages = localAttachments.filter(
        (image) => image.key !== attachment.key
      );
      setLocalAttachments(filteredImages);

      // Notify parent component for ____________ attachments
      if (additionalData === '' && onLocalAttachmentsChange) {
        onLocalAttachmentsChange(filteredImages);
      }

      message.success('Local attachment removed successfully');
    } else {
      const attachmentId = attachment.key || attachment.id;
      setDelAttachKey([...delAttachKey, attachmentId]);

      try {
        if (recordType === "variance_report") {
          const reportId = record?.id || id;
          await dispatch(actions.deleteVarianceAttachment(registerId, reportId, attachmentId));
        } else {
          await axios.delete(deleteUrl(attachmentId));
          message.success('Attachment deleted successfully');

          // Remove from local state
          const filteredImages = localAttachments.filter(
            (image) => (image.key || image.id) !== attachmentId
          );
          setLocalAttachments(filteredImages);
        }
      } catch (error) {
        console.error('Delete error:', error);
        message.error('Failed to delete attachment');
      } finally {
        setDelAttachKey(delAttachKey.filter(key => key !== attachmentId));
      }
    }
  };

  return (
    <div className="progress-step-attachments">
      <div className="edit-icon-div" style={{ margin: '0px' }}>
        {(!visibility && canAdd) && (
          <>
            <input
              disabled={uploading || disabled}
              type="file"
              name="attachment"
              id={inputId}
              accept="doc/docx/xls/xlsx/ppt/pptx/pdf/jpg/jpeg/gif/png/mp4"
              style={{ display: "none" }}
              onChange={changeHandler}
              multiple
              ref={inputFile}
            />
            <label htmlFor={inputId}>
              <AttachmentButton
                onClick={() => setModalVisible(true)}
                loading={uploading}
                style={{ color, borderColor }}
                count={localAttachments.length}
                disabled={disabled || (!record && additionalData !== '')}
                visibility={visibility}
              />
            </label>
          </>
        )}
        {(!visibility && !canAdd) && (
          <AttachmentButton
            onClick={() => setModalVisible(true)}
            loading={uploading}
            style={{ color, borderColor }}
            count={localAttachments.length}
            disabled={disabled || (!record && additionalData !== '')}
            visibility={visibility}
          />
        )}
        {visibility && (
          <AttachmentButton
            onClick={() => setModalVisible(true)}
            loading={uploading}
            style={{ color, borderColor }}
            count={localAttachments.length}
            disabled={disabled || (!record && additionalData !== '')}
            visibility={visibility}
          />
        )}
      </div>

      <AttachmentsListModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        attachments={localAttachments}
        onShowImage={handleShowImage}
        onDeleteImage={handleDeleteImage}
        delAttachKey={delAttachKey}
        title={modalTitle}
        onUpload={changeHandler}
        uploading={uploading}
        canAdd={canAdd}
      />
    </div>
  );
};

export default AttachmentContainer;
